import type { AIAnswerQueryStreamTraceType, SAINResearchStep } from '../types';

/**
 * Merge a single rich `RESEARCH_STEP_*` trace event into the local research-step
 * state. Mirrors the per-message-template branching used by Chat's
 * `chat-message-research-store.processStreamResearch` action, but operates on a
 * plain `{ [stepId]: SAINResearchStep }` map managed by `React.useState`.
 *
 * Supported templates:
 * - RESEARCH_STEP_TITLE / RESEARCH_STEP_TITLE_SHORT  → create/update step with title
 * - RESEARCH_STEP_CONTENT                            → append content chunks
 * - RESEARCH_STEP_SOURCE                             → attach sources
 * - RESEARCH_STEP_ICONS                              → attach product icons
 * - RESEARCH_STEP_COMPLETE                           → mark step completed
 */
export const mergeResearchStepTrace = (
	prev: { [stepId: string]: SAINResearchStep },
	traceData: NonNullable<AIAnswerQueryStreamTraceType['traceData']>,
): { [stepId: string]: SAINResearchStep } => {
	const { messageTemplate, stepId } = traceData;
	if (!messageTemplate || !stepId) {
		return prev;
	}

	// `existing` is the current state of this step (if it has arrived before,
	// e.g. TITLE arrived and now CONTENT is streaming in). `isNewStep` flags
	// that this is the very first message for this stepId.
	// `order` preserves the insertion sequence across all steps so the UI can
	// sort them chronologically — re-uses the existing order if already set,
	// otherwise takes the next available integer after the highest current order.
	// `baseStep` merges existing state with required defaults so every switch
	// branch below can safely spread it and only override what changed.
	const existing = prev[stepId];
	const isNewStep = !existing;
	const order =
		existing?.order ??
		(Object.values(prev).length ? Math.max(...Object.values(prev).map((s) => s.order)) + 1 : 1);
	const baseStep: SAINResearchStep = {
		...existing,
		stepId,
		roundId: existing?.roundId ?? traceData.roundId ?? 0,
		order,
		separatedContent: existing?.separatedContent ?? [],
	};

	switch (messageTemplate) {
		case 'RESEARCH_STEP_TITLE':
		case 'RESEARCH_STEP_TITLE_SHORT': {
			return {
				...prev,
				[stepId]: {
					...baseStep,
					title: traceData.title,
					roundId: traceData.roundId ?? baseStep.roundId,
					toolIcon: traceData.toolIcon ?? undefined,
					toolName: traceData.toolName ?? undefined,
					completed: baseStep.completed ?? false,
				},
			};
		}
		case 'RESEARCH_STEP':
		case 'RESEARCH_STEP_CONTENT': {
			const chunk = traceData.content ?? '';
			const concatenated = (baseStep.content ?? '') + chunk;
			return {
				...prev,
				[stepId]: {
					...baseStep,
					roundId: traceData.roundId ?? baseStep.roundId,
					content: concatenated,
					separatedContent: [...(baseStep.separatedContent ?? []), chunk],
					// Orphan content (content arriving for a step that has no title yet) is
					// considered already-complete to avoid hanging in an unfinished state.
					// Also preserve completed=true if RESEARCH_STEP_COMPLETE arrived out-of-order.
					completed: baseStep.completed ?? (isNewStep && !baseStep.title),
				},
			};
		}
		case 'RESEARCH_STEP_SOURCE': {
			return {
				...prev,
				[stepId]: {
					...baseStep,
					sources: traceData.sources ?? baseStep.sources,
				},
			};
		}
		case 'RESEARCH_STEP_ICONS': {
			const ICON_BASE_URL = '/gateway/api/assist/rovo/v1/resources/icon/';
			const products = traceData.products ?? [];
			return {
				...prev,
				[stepId]: {
					...baseStep,
					sourceIcons: products.map((p) =>
						p.startsWith('/') || p.startsWith('data:') ? p : `${ICON_BASE_URL}${p}`,
					),
				},
			};
		}
		case 'RESEARCH_STEP_COMPLETE': {
			return {
				...prev,
				[stepId]: {
					...baseStep,
					completed: true,
				},
			};
		}
		default:
			return prev;
	}
};
